import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

// Components
import RoleItem from "../../../../lists/generalBranchSettings/Role.item";
import { Header, Button, Body, CentralModal, Card, Search } from "../../../../UI";
import { Heading2 } from "../../../../typography";
import ConfirmDialog from "../../../../UI/ConfirmDialog/ConfirmDialog";

// Containers
import AddRole from "../../../../../containers/forms/generalBranchSettingsForms/RolesAndPermissions/AddRole/AddRole";
import EditRole from "../../../../../containers/forms/generalBranchSettingsForms/RolesAndPermissions/EditRole/EditRole";

// Builders
import * as buttonDesignType from "../../../../../builders/buttonDesign.types";

// Constants

// Calls
import {
  fetchRolesRequest,
  deleteRoleRequest
} from "../../../../../api/calls/general-branch-settings.call";
import { fetchPermissionsRequest } from "../../../../../api/calls/permissions.call";

// Utils
import { isEmpty } from "../../../../../utils";

// Styles
import { RolesAndPermissionsStyles } from "./rolesAndPermissions.styles";

const ADD_ROLE = "ADD_ROLE";
const EDIT_ROLE = "EDIT_ROLE";

const RolesAndPermissions = ({ branchId }) => {
  const [rolesSectionState, setRolesSectionState] = useState({
    roles: [],
    permissions: [],
    chosenRole: undefined,
    modalType: undefined,
    searchQuery: "",
    confirmDelete: { isOpen: false, roleId: null }
  });

  const [deletingId, setDeletingId] = useState(null);

  const fetchRolesAndPermissions = useCallback(async () => {
    const rolesAndPermissionsResponse = await Promise.all([
      fetchRolesRequest(branchId),
      fetchPermissionsRequest(["all", "staff"])
    ]);
    const rolesResponse = rolesAndPermissionsResponse[0];
    const permissionsResponse = rolesAndPermissionsResponse[1];
    if (rolesResponse.hasError) {
      toast.error(rolesResponse.error.message);
      return;
    }

    if (permissionsResponse.hasError) {
      toast.error(permissionsResponse.error.message);
      return;
    }
    setRolesSectionState(prevValues => ({
      ...prevValues,
      permissions: permissionsResponse.permissions.map(permission => {
        return { ...permission, id: parseInt(permission.id) };
      }),
      roles: rolesResponse.roles
    }));
  }, [branchId]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, [fetchRolesAndPermissions]);

  const openDeleteConfirm = id => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      confirmDelete: { isOpen: true, roleId: id }
    }));
  };

  const closeDeleteConfirm = () => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      confirmDelete: { isOpen: false, roleId: null }
    }));
  };

  const confirmDeleteRole = async () => {
    const { roleId } = rolesSectionState.confirmDelete;
    const response = await deleteRoleRequest(branchId, roleId);
    if (response.hasError) {
      toast.error(response.error.message);
      closeDeleteConfirm();
      return;
    }
    toast.success("Role successfully deleted.");
    closeDeleteConfirm();
    setDeletingId(roleId);
    setTimeout(() => {
      setDeletingId(null);
      setRolesSectionState(prevValues => ({
        ...prevValues,
        roles: response.roles
      }));
    }, 300);
  };

  const addRoleModalOpen = () => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      modalType: ADD_ROLE
    }));
  };

  const editRoleModalOpen = role => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      chosenRole: role,
      modalType: EDIT_ROLE
    }));
  };

  const closeModal = () => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      chosenRole: undefined,
      modalType: undefined
    }));
  };

  const setRolesAfterCreateAndUpdateRole = roles => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      roles,
      chosenRole: undefined,
      modalType: undefined
    }));
  };

  const handleSearch = e => {
    setRolesSectionState(prevValues => ({
      ...prevValues,
      searchQuery: e.target.value
    }));
  };

  const filterRoles = query => {
    const q = query.toLowerCase();
    return rolesSectionState.roles.filter(
      role =>
        role.name.toLowerCase().includes(q) ||
        role.permissions.some(p => p.name.toLowerCase().includes(q))
    );
  };

  const filteredRoles = filterRoles(rolesSectionState.searchQuery);

  return (
    <RolesAndPermissionsStyles>
      <Card rounded className="headerCard">
        <Header>
          <div className="header">
            <Heading2>ROLES & PERMISSIONS</Heading2>
            <Button
              onClick={addRoleModalOpen}
              className="addRole"
              buttonDesignType={buttonDesignType.PRIMARY}
            >
              Add New Role
            </Button>
          </div>
        </Header>
        <Body>
          <div className="searchBar">
            <div className="searchContainer">
              <Search
                placeholder="Search roles"
                value={rolesSectionState.searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>
        </Body>
      </Card>
      {!isEmpty(filteredRoles) && (
        <div className="rolesList">
          {filteredRoles.map(role => (
            <Card rounded className={`roleCard${deletingId === role.id ? " deleting" : ""}`} key={role.id}>
              <RoleItem
                {...role}
                deleteAction={() => openDeleteConfirm(role.id)}
                editAction={() => editRoleModalOpen(role)}
              />
            </Card>
          ))}
        </div>
      )}
      <ConfirmDialog
        isOpen={rolesSectionState.confirmDelete.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDeleteRole}
        title="Delete Role?"
        message="Are you sure you want to delete this role?"
        confirmText="Delete"
        danger
      />
      <CentralModal
        isOpen={!!rolesSectionState.modalType}
        closeModal={closeModal}
        width={620}
      >
        <Card>
            {rolesSectionState.modalType === ADD_ROLE && (
              <AddRole
                setRolesAfterCreateAndUpdateRole={
                  setRolesAfterCreateAndUpdateRole
                }
                branchId={branchId}
                permissions={rolesSectionState.permissions}
                closeModal={closeModal}
              />
            )}
            {rolesSectionState.modalType === EDIT_ROLE && (
              <EditRole
                setRolesAfterCreateAndUpdateRole={
                  setRolesAfterCreateAndUpdateRole
                }
                chosenRole={rolesSectionState.chosenRole}
                branchId={branchId}
                permissions={rolesSectionState.permissions}
                closeModal={closeModal}
              />
            )}
          </Card>
      </CentralModal>
    </RolesAndPermissionsStyles>
  );
};

export default RolesAndPermissions;
